<script setup>
import FadeTransition from "@/components/transitions/FadeTransition.vue";
import {useQuery} from "@tanstack/vue-query";
import {ref} from "vue";
import axios from "axios";
import useTypewriter from "@/composables/useTypewriter";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import {Steps} from "@/composables/ai-onboarding/useSteps";
import useKit from "@/composables/useKit";

const {canSeeKit} = useKit();

const props = defineProps({
  aiSummaryUrlPath: {
    type: String,
    required: true,
  },
  aiSitemap: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const typingSpeed = 25; // ms

const emit = defineEmits(['typewriter-finished']);

const { typedText, startTyping } = useTypewriter(emit);

let queryKey = ['ai-summary-message'];
if (props.aiSitemap) {
  queryKey.push(Steps.value.AI_AUTO_ASSIGN_HOLDING.key);
} else {
  queryKey.push(Steps.value.HOLDING.key);
}

const enabled = ref(true);

const { data: aiSummaryMessage } = useQuery({
  queryKey: queryKey,
  queryFn: async () => {
    const {data} = await axios.get(props.aiSummaryUrlPath);

    const fullText = data.data;
    startTyping(fullText, typingSpeed);

    enabled.value = false;
    return fullText;
  },
  enabled: () => enabled.value
});
</script>

<template>
  <FadeTransition>
    <div
      v-if="aiSummaryMessage"
      class="tw-rounded-4xl tw-bg-gradient-to-b tw-from-lightGreyGradientStart tw-to-lightGreyGradientEnd tw-to tw-px-8 tw-py-10 tw-border-b-2 tw-border-grey-mid tw-relative tw-max-w-4xl tw-w-full"
    >
      <div
        class="tw-bg-primary-100 tw-rounded-4xl tw-absolute tw-left-0 tw-top-[-32px] tw-px-4 tw-items-center tw-justify-center tw-flex tw-shadow-xl"
      >
        <div class="tw-flex tw-items-center tw-gap-7 tw-h-[60px]">
          <KitIcon
            v-if="canSeeKit"
            class="tw-w-auto !tw-h-11"
          />
          <span
            v-else
            class="tw-text-4.5xl"
          >
            🤖
          </span>
          <span class="tw-whitespace-nowrap tw-font-bold tw-text-neutral-900 tw-text-xl">
            {{ aiSitemap ? 'Why I chose these pages?' : 'Why I chose these keywords?' }}
          </span>
        </div>
      </div>
      <p class="tw-text-base tw-text-left tw-text-slate-200 tw-w-full">
        {{ typedText }}
      </p>
    </div>
  </FadeTransition>
</template>
